import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { Trash2 } from 'lucide-react';
import { Control } from 'react-hook-form';
import { FormFieldWrapper } from '../../../FormFieldWrapper';
import { IdentificationFormData } from '../types';

interface PepCardProps {
  control: Control<IdentificationFormData>;
  index: number;
  onRemove: () => void;
  canRemove: boolean;
}

export function PepCard({ control, index, onRemove, canRemove }: PepCardProps) {
  const { t: tForms } = useFormsTranslation();

  return (
    <div className='space-y-4 border rounded-lg p-4'>
      <div className='flex justify-between items-center mb-4'>
        <h5 className='text-sm font-medium'>
          {tForms('identification.labels.pepNumber', {
            number: index + 1,
          })}
        </h5>
        {canRemove && (
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={onRemove}
            className='text-destructive hover:text-destructive'
          >
            <Trash2 className='w-4 h-4 mr-1' />
            {tForms('identification.buttons.remove')}
          </Button>
        )}
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name={`politicallyExposedPersons.${index}.lastName` as const}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('identification.fields.pepLastName')}
              name={`politicallyExposedPersons-${index}-lastName`}
              required
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms('identification.placeholders.pepLastName')}
                className='min-h-[44px]'
              />
            </FormFieldWrapper>
          )}
        />

        <FormField
          control={control}
          name={`politicallyExposedPersons.${index}.firstName` as const}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('identification.fields.pepFirstName')}
              name={`politicallyExposedPersons-${index}-firstName`}
              required
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms('identification.placeholders.pepFirstName')}
                className='min-h-[44px]'
              />
            </FormFieldWrapper>
          )}
        />
      </div>

      <div className='grid grid-cols-1 gap-4 items-start'>
        <FormField
          control={control}
          name={`politicallyExposedPersons.${index}.pepReason` as const}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('identification.fields.pepReason')}
              name={`politicallyExposedPersons-${index}-pepReason`}
              required
              error={fieldState.error?.message}
            >
              <Textarea
                {...field}
                placeholder={tForms('identification.placeholders.pepReason')}
                className='min-h-[100px] resize-vertical'
                rows={4}
              />
            </FormFieldWrapper>
          )}
        />
      </div>
    </div>
  );
}
